/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import { Tooltip } from '../Tooltip';
import type { IconButtonProps } from '../../types';
import Icon from '../Icon/Icon';
import './IconButton.less';

const Spinner = () => (
  <svg className="icon-button__spinner" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      disabled = false,
      loading = false,
      icon,
      'aria-label': ariaLabel,
      onClick,
      className,
      tooltip,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) return;
      if (onClick) {
        onClick(e);
      }
    };

    const button = (
      <button
        ref={ref}
        type="button"
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        aria-label={ariaLabel}
        onClick={handleClick}
        {...rest}
        className={cx(
          'icon-button',
          `icon-button--${variant}`,
          `icon-button--${size}`,
          { 'icon-button--loading': loading },
          className
        )}>
        {/* Original icon — always rendered to preserve size, invisible when loading */}
        <span className={cx('icon-button__icon', { 'icon-button__icon--hidden': loading })}>
          <Icon name={icon} />
        </span>

        {/* Spinner overlay — absolutely centered */}
        {loading && (
          <span className="icon-button__spinner-overlay">
            <Spinner />
          </span>
        )}
      </button>
    );

    if (tooltip) {
      return <Tooltip content={tooltip}>{button}</Tooltip>;
    }

    return button;
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
